// src/components/TicketCard.jsx

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FaEllipsisV } from 'react-icons/fa';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import apiClient from '../../../../../apiClient/apiClient';

const TicketCard = ({ ticket, onEdit, onDeleteTicket }) => {
  const sold = ticket?.ticket_sold || 0;
  const gross = (Number(ticket?.price) || 0) * sold;

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const cardRef = useRef(null);

  const accessToken = localStorage.getItem("access") || sessionStorage.getItem("access");
  const config = {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
  };

  const handleDelete = async () => {
    console.log(`Sending DELETE request for ticket ID: ${ticket.id}`);
    try {
      const response = await apiClient.delete(`/ticket/${ticket.id}`, config);
      console.log('Delete response:', response);
      onDeleteTicket(ticket.id);
    } catch (error) {
      console.error('Error deleting ticket:', error);
    }
    setIsDeleteModalOpen(false);
    setIsMenuOpen(false);
  };

  // Handle outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  return (
    <div
      ref={cardRef}
      className="p-4 border rounded bg-white hover:bg-gray-50 transition flex justify-between items-center relative cursor-pointer"
      onClick={() => {
        if (!isMenuOpen) {
          setIsMenuOpen(true);
        }
      }}
    >
      <div>
        <p className="font-semibold">{ticket.ticket_category}</p>
        <p className="text-gray-600">Price: £{ticket.price}</p>
        <p className="text-gray-600">Total Quantity: {ticket.total_quantity}</p>
        <p className="text-gray-600">Description: {ticket.ticket_description}</p>
      </div>
      <div className="text-right flex items-center space-x-2">
        <div>
          <p>Sold: {sold}</p>
          <p>Gross: £{gross}</p>
        </div>

        {/* Three-dot menu button */}
        <button
          onClick={(e) => {
            e.stopPropagation(); // Prevent card's onClick from triggering
            setIsMenuOpen(!isMenuOpen);
          }}
          className="p-2 rounded-full hover:bg-gray-100 focus:outline-none"
        >
          <FaEllipsisV />
        </button>

        {/* Dropdown Menu */}
        {isMenuOpen && (
          <div className="absolute right-4 top-12 bg-white border rounded shadow-lg z-10 w-40">
            <button
              onClick={() => {
                onEdit(ticket);
                setIsMenuOpen(false);
              }}
              className="w-full text-left px-4 py-2 hover:bg-gray-100"
            >
              Edit ticket
            </button>
            <button
              onClick={() => {
                setIsMenuOpen(false);
                setIsDeleteModalOpen(true);
              }}
              className="w-full text-left px-4 py-2 hover:bg-gray-100 text-red-600"
            >
              Delete ticket
            </button>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onRequestClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        eventName={ticket.ticket_category}
        type={"ticket"}
      />
    </div>
  );
};

TicketCard.propTypes = {
  ticket: PropTypes.shape({
    id: PropTypes.number.isRequired,
    ticket_category: PropTypes.string.isRequired,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    total_quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    ticket_sold: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDeleteTicket: PropTypes.func.isRequired,
};

export default TicketCard;
